import React, { useState, useEffect } from 'react';
import InvoiceTemplate from './InvoiceTemplate';
import {
  migrateInvoicesFromLocalStorage,
  subscribeInvoices,
  saveInvoice,
  deleteInvoice,
} from '../../../services/firestoreInvoices';
import './InvoicesManager.css';

const InvoicesManager = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showInvoiceTemplate, setShowInvoiceTemplate] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [useCustomInvoiceId, setUseCustomInvoiceId] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    invoiceNumber: '',
    customInvoiceId: '',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    date: new Date().toISOString().split('T')[0],
    items: [{ description: '', price: 0 }],
    tax: 0,
    discount: 0,
    notes: '',
    status: 'draft',
    useTax: true // Default to using tax
  });

  const predefinedServices = [
    { name: 'Website Development', price: 2500, description: 'Custom website development with responsive design' },
    { name: 'Mobile App Development', price: 5000, description: 'Cross-platform mobile application development' },
    { name: 'E-commerce Platform', price: 3500, description: 'Full-featured online store with payment integration' },
    { name: 'API Development', price: 1500, description: 'RESTful API development and documentation' },
    { name: 'Database Design', price: 800, description: 'Database architecture and optimization' },
    { name: 'UI/UX Design', price: 1200, description: 'User interface and experience design' },
    { name: 'Maintenance & Support', price: 500, description: 'Monthly maintenance and technical support' },
    { name: 'SEO Optimization', price: 300, description: 'Search engine optimization services' }
  ];

  useEffect(() => {
    let unsubscribe;

    async function init() {
      try {
        await migrateInvoicesFromLocalStorage();
        unsubscribe = subscribeInvoices((list) => {
          setInvoices(list);
          setLoading(false);
        });
      } catch (err) {
        console.error('Invoices sync init failed', err);
        setSaveError('Could not load invoices. Check connection.');
        setLoading(false);
      }
    }

    init();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const generateInvoiceNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `INV-${year}${month}${day}-${random}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError(null);
    try {
      const invoiceNumber = useCustomInvoiceId && formData.customInvoiceId
        ? formData.customInvoiceId
        : (formData.invoiceNumber || generateInvoiceNumber());
      const payload = {
        ...formData,
        id: editingInvoice ? editingInvoice.id : Date.now().toString(),
        invoiceNumber,
        createdAt: editingInvoice ? editingInvoice.createdAt : new Date().toISOString(),
      };
      await saveInvoice(payload);
      resetForm();
      setShowModal(false);
    } catch (err) {
      console.error('Save invoice failed', err);
      setSaveError('Failed to save invoice. Try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      invoiceNumber: '',
      customInvoiceId: '',
      clientName: '',
      clientEmail: '',
      clientPhone: '',
      date: new Date().toISOString().split('T')[0],
      items: [{ description: '', price: 0 }],
      tax: 0,
      discount: 0,
      notes: '',
      status: 'draft',
      useTax: true // Default to using tax
    });
    setEditingInvoice(null);
    setUseCustomInvoiceId(false);
  };

  const handleEdit = (invoice, e) => {
    e.preventDefault();
    e.stopPropagation();
    setFormData(invoice);
    setEditingInvoice(invoice);
    // Check if the invoice has a custom invoice ID (not auto-generated format)
    const isCustomId = invoice.invoiceNumber && !invoice.invoiceNumber.startsWith('INV-');
    setUseCustomInvoiceId(isCustomId);
    if (isCustomId) {
      setFormData(prev => ({ ...prev, customInvoiceId: invoice.invoiceNumber }));
    }
    setShowModal(true);
  };

  const handleViewInvoice = (invoice, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedInvoice(invoice);
    setShowInvoiceTemplate(true);
  };

  const handleDelete = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    setSaveError(null);
    try {
      await deleteInvoice(id);
    } catch (err) {
      console.error('Delete invoice failed', err);
      setSaveError('Failed to delete invoice. Try again.');
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', price: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.price || 0), 0);
    const taxAmount = formData.useTax ? subtotal * 0.11 : 0; // Use tax only if useTax is true
    const discountAmount = subtotal * (formData.discount / 100);
    return subtotal + taxAmount - discountAmount;
  };

  return (
    <div className="invoices-manager">
      <div className="invoices-header">
        <h2>Invoices Management</h2>
        <button onClick={() => setShowModal(true)} className="add-invoice-btn" disabled={loading}>
          + Create New Invoice
        </button>
      </div>

      {saveError && (
        <div className="invoices-error" role="alert">
          {saveError}
        </div>
      )}

      {loading ? (
        <div className="invoices-loading">Loading invoices…</div>
      ) : (
      <div className="invoices-grid">
        {invoices.map(invoice => (
          <div key={invoice.id} className="invoice-card">
            <div className="invoice-header">
              <h3>{invoice.invoiceNumber}</h3>
              <span className={`status ${invoice.status}`}>{invoice.status}</span>
            </div>
            <div className="invoice-details">
              <p><strong>Client:</strong> {invoice.clientName}</p>
              <p><strong>Date:</strong> {invoice.date}</p>
              <p><strong>Total:</strong> ${calculateInvoiceTotal(invoice).toFixed(2)}</p>
            </div>
            <div className="invoice-actions">
              <button onClick={(e) => handleViewInvoice(invoice, e)} className="view-btn">View Invoice</button>
              <button onClick={(e) => handleEdit(invoice, e)} className="edit-btn">Edit</button>
              <button onClick={(e) => handleDelete(invoice.id, e)} className="delete-btn">Delete</button>
            </div>
          </div>
        ))}
      </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content invoice-modal-content">
            <div className="modal-header">
              <h3>{editingInvoice ? 'Edit Invoice' : 'Create New Invoice'}</h3>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="close-btn">×</button>
            </div>
            <form onSubmit={handleSubmit} className="invoice-form">
              {/* --- Client Info Section --- */}
              <div className="section-heading">
                <span>Client Info</span>
                <div className="section-divider"></div>
              </div>
              
              {/* Invoice ID Section */}
              <div className="invoice-id-section">
                <div 
                  className="invoice-id-toggle"
                  onClick={() => {
                    setUseCustomInvoiceId(!useCustomInvoiceId);
                    if (useCustomInvoiceId) {
                      setFormData({ ...formData, customInvoiceId: '' });
                    }
                  }}
                >
                  <div className={`toggle-switch ${useCustomInvoiceId ? 'active' : ''}`}>
                    <div className="toggle-slider"></div>
                  </div>
                  <div>
                    <div className="toggle-label">Use Custom Invoice ID</div>
                    <div className="toggle-description">
                      {useCustomInvoiceId ? 'Enter your custom invoice ID format' : 'Auto-generate invoice ID (INV-YYYYMMDD-XXX)'}
                    </div>
                  </div>
                </div>
                
                {useCustomInvoiceId && (
                  <div className="custom-invoice-input-wrapper">
                    <input
                      type="text"
                      value={formData.customInvoiceId}
                      onChange={(e) => setFormData({ ...formData, customInvoiceId: e.target.value })}
                      placeholder="Enter custom invoice ID (e.g., INV-2024-001, SKRIPT-WEB-001)"
                      required={useCustomInvoiceId}
                      className="custom-invoice-input"
                    />
                    {formData.customInvoiceId && (
                      <div className="invoice-id-preview">
                        {formData.customInvoiceId}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Client Name</label>
                  <input
                    type="text"
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Client Email</label>
                  <input
                    type="email"
                    value={formData.clientEmail}
                    onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Client Phone</label>
                  <input
                    type="tel"
                    value={formData.clientPhone}
                    onChange={(e) => setFormData({ ...formData, clientPhone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* --- Items Section --- */}
              <div className="section-heading">
                <span>Items</span>
                <div className="section-divider"></div>
              </div>
              <div className="items-section">
                {/* Predefined Services */}
                <div className="predefined-services">
                  <h5>Quick Add Services:</h5>
                  <div className="services-grid">
                    {predefinedServices.map((service, index) => (
                      <button
                        key={index}
                        type="button"
                        className="service-btn service-card"
                        aria-label={`Add ${service.name}`}
                        onClick={() => {
                          const newItems = [...formData.items];
                          newItems.push({
                            description: service.description,
                            price: service.price
                          });
                          setFormData({ ...formData, items: newItems });
                        }}
                      >
                        <span className="service-icon">➕</span>
                        <span className="service-name">{service.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Items Table */}
                <div className="items-table-wrapper">
                  <table className="items-table-modal">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Price</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.items.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="text"
                              placeholder="Service description"
                              value={item.description}
                              onChange={(e) => updateItem(index, 'description', e.target.value)}
                              required
                              className="item-desc-input"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              placeholder="0.00"
                              value={item.price || ''}
                              onChange={(e) => updateItem(index, 'price', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                              className="item-price-input"
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="remove-item-btn"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button type="button" onClick={addItem} className="add-item-btn">
                    + Add Custom Item
                  </button>
                </div>
              </div>

              {/* --- Summary Section --- */}
              <div className="section-heading">
                <span>Summary</span>
                <div className="section-divider"></div>
              </div>
              
              {/* Tax Toggle */}
              <div className="tax-toggle-section">
                <div 
                  className="tax-toggle"
                  onClick={() => setFormData({ ...formData, useTax: !formData.useTax })}
                >
                  <div className="tax-toggle-row">
                    <div className={`toggle-switch ${formData.useTax ? 'active' : ''}`}>
                      <div className="toggle-slider"></div>
                    </div>
                    <div className="toggle-label">Apply Tax (11%)</div>
                  </div>
                  <div className="toggle-description">
                    {formData.useTax ? 'Tax will be applied to the total amount' : 'No tax will be applied to this invoice'}
                  </div>
                </div>
              </div>
              
              <div className="summary-card">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>${formData.items.reduce((sum, item) => sum + (item.price || 0), 0).toFixed(2)}</span>
                </div>
                {formData.useTax && (
                  <div className="summary-row">
                    <span>Tax (11%)</span>
                    <span>${(formData.items.reduce((sum, item) => sum + (item.price || 0), 0) * 0.11).toFixed(2)}</span>
                  </div>
                )}
                <div className="summary-row">
                  <span>Discount (%)</span>
                  <input
                    type="number"
                    value={formData.discount}
                    onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) })}
                    min="0"
                    step="0.01"
                    className="summary-input"
                  />
                </div>
                <div className="summary-row total-row">
                  <span>Total</span>
                  <span>${calculateTotal().toFixed(2)}</span>
                </div>
              </div>

              {/* --- Notes & Status --- */}
              <div className="section-heading">
                <span>Other</span>
                <div className="section-divider"></div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="2"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              {/* --- Actions --- */}
              <div className="form-actions">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  {editingInvoice ? 'Update Invoice' : 'Create Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showInvoiceTemplate && selectedInvoice && (
        <InvoiceTemplate 
          invoice={selectedInvoice}
          onClose={() => {
            setShowInvoiceTemplate(false);
            setSelectedInvoice(null);
          }}
        />
      )}
    </div>
  );
};

const calculateInvoiceTotal = (invoice) => {
  const subtotal = invoice.items.reduce((sum, item) => sum + (item.price || 0), 0);
  const taxAmount = invoice.useTax !== false ? subtotal * 0.11 : 0; // Use tax unless explicitly disabled
  const discountAmount = subtotal * (invoice.discount / 100);
  return subtotal + taxAmount - discountAmount;
};

export default InvoicesManager;

